/**
 * Minimal EXIF DateTimeOriginal reader for JPEGs (the only container that
 * carries EXIF in practice on iOS / Android / DSLR uploads). Runs entirely
 * client-side against a data URL so no image bytes ever leave the device
 * unless we upload them for identification. Returns `null` when EXIF is
 * missing / stripped (screenshots, iOS Share, WhatsApp, PNG/WebP, etc.),
 * letting callers fall back to the upload timestamp.
 *
 * We avoid pulling in a full EXIF library (~40KB) because we only need one
 * tag. The parser walks the JPEG segment list looking for the APP1 EXIF
 * marker (0xFFE1), then does a targeted TIFF IFD0 scan for tag 0x9003
 * (DateTimeOriginal). Anything else it sees is skipped — corrupt EXIF
 * bytes never throw, they return `null`.
 */

// EXIF DateTimeOriginal has the format "YYYY:MM:DD HH:MM:SS" — we convert
// to ISO "YYYY-MM-DDTHH:MM:SS" so sort order in Mongo / CSV / gallery is
// lexicographic-safe.
const EXIF_DATE_RE = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/;
const isoFromExif = (raw) => {
  const m = EXIF_DATE_RE.exec(raw);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`;
};

// Convert a data URL / base64 string to a raw Uint8Array we can index into.
const bytesFromDataUrl = (dataUrl) => {
  if (typeof dataUrl !== "string") return null;
  const comma = dataUrl.indexOf(",");
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  try {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
};

/**
 * @param {string} dataUrl — the full data:image/jpeg;base64,... string.
 * @returns {string | null} ISO string "YYYY-MM-DDTHH:MM:SS" or null.
 */
export const extractExifDateTaken = (dataUrl) => {
  const bytes = bytesFromDataUrl(dataUrl);
  if (!bytes || bytes.length < 20) return null;
  if (bytes[0] !== 0xFF || bytes[1] !== 0xD8) return null; // not a JPEG SOI

  let i = 2;
  while (i < bytes.length - 4) {
    if (bytes[i] !== 0xFF) return null; // malformed
    const marker = bytes[i + 1];
    if (marker === 0xDA) return null; // SOS — image data starts, we're done
    const segLen = (bytes[i + 2] << 8) | bytes[i + 3];
    if (segLen < 2) return null;

    if (marker === 0xE1) {
      // APP1 — verify EXIF magic "Exif\0\0"
      const start = i + 4;
      if (bytes[start] === 0x45 && bytes[start + 1] === 0x78 &&
          bytes[start + 2] === 0x69 && bytes[start + 3] === 0x66) {
        const tiff = start + 6;
        const little = bytes[tiff] === 0x49 && bytes[tiff + 1] === 0x49;
        const big = bytes[tiff] === 0x4D && bytes[tiff + 1] === 0x4D;
        if (!little && !big) return null;

        const u16 = (off) => little
          ? bytes[off] | (bytes[off + 1] << 8)
          : (bytes[off] << 8) | bytes[off + 1];
        const u32 = (off) => little
          ? bytes[off] | (bytes[off + 1] << 8) | (bytes[off + 2] << 16) | (bytes[off + 3] << 24)
          : (bytes[off] << 24) | (bytes[off + 1] << 16) | (bytes[off + 2] << 8) | bytes[off + 3];

        const ifd0 = tiff + u32(tiff + 4);
        const scanIfd = (ifdOff) => {
          if (ifdOff <= 0 || ifdOff + 2 > bytes.length) return null;
          const count = u16(ifdOff);
          for (let e = 0; e < count; e++) {
            const entry = ifdOff + 2 + e * 12;
            if (entry + 12 > bytes.length) break;
            const tag = u16(entry);
            // 0x9003 = DateTimeOriginal (Exif IFD); 0x0132 = DateTime (IFD0 fallback)
            if (tag === 0x9003 || tag === 0x0132) {
              const dataOff = u32(entry + 8);
              const absOff = tiff + dataOff;
              if (absOff + 19 > bytes.length) continue;
              let raw = "";
              for (let k = 0; k < 19; k++) raw += String.fromCharCode(bytes[absOff + k]);
              const iso = isoFromExif(raw);
              if (iso) return iso;
            }
            // 0x8769 = Exif IFD pointer — jump to sub-IFD which contains 0x9003.
            if (tag === 0x8769) {
              const subOff = tiff + u32(entry + 8);
              const found = scanIfd(subOff);
              if (found) return found;
            }
          }
          return null;
        };
        return scanIfd(ifd0);
      }
    }
    i += 2 + segLen;
  }
  return null;
};

/**
 * Best-effort helper for callers that want an ISO date string OR null.
 * Wraps `extractExifDateTaken` in a try/catch so unexpected data can never
 * break an upload flow — worst case we just fall back to upload timestamp.
 */
export function safeExifDateTaken(dataUrl) {
  try {
    return extractExifDateTaken(dataUrl);
  } catch {
    return null;
  }
}
